import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, memo, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import { CurrentLineObserver } from "~/components/AssetModalV2/Content/Panel/Transcript/CurrentLineObserver";
import { useFetchCurrentTranscriptChunk } from "~/components/AssetModalV2/Content/Panel/Transcript/hooks/useFetchCurrentTranscriptChunk";
import { useFetchSearchResultsForSegmentsInView } from "~/components/AssetModalV2/Content/Panel/Transcript/hooks/useFetchSearchResultsForSegmentsInView";
import { useOnUserListScroll } from "~/components/AssetModalV2/Content/Panel/Transcript/hooks/useOnUserListScroll";
import { LoadingTranscriptSegments } from "~/components/AssetModalV2/Content/Panel/Transcript/LoadingTranscriptSegments";
import { TranscriptSegment } from "~/components/AssetModalV2/Content/Panel/Transcript/TranscriptSegment";
import { TranscriptSegmentsVirtualizedList } from "~/components/AssetModalV2/Content/Panel/Transcript/TranscriptSegmentsVirtualizedList";
import { convertTranscriptRangesToFlatSegments } from "~/components/AssetModalV2/Content/Panel/Transcript/utils";
import { useTranscriptScrollInfoContext } from "~/providers/TranscriptScrollInfoProvider";
export const TranscriptSegmentsList = /*#__PURE__*/ memo(/*#__PURE__*/ forwardRef((param, forwardedRef)=>{
    let { transcriptRanges, hasMore, durationMs, fetchTranscriptForRange } = param;
    const listRef = useRef(null);
    const currentWordObserverRef = useRef(null);
    useImperativeHandle(forwardedRef, ()=>({
            returnToCurrentWord: ()=>{
                var _currentWordObserverRef_current;
                (_currentWordObserverRef_current = currentWordObserverRef.current) === null || _currentWordObserverRef_current === void 0 ? void 0 : _currentWordObserverRef_current.returnToCurrentWord();
            }
        }));
    const segments = useMemo(()=>convertTranscriptRangesToFlatSegments({
            transcript: transcriptRanges,
            durationMs,
            hasMore
        }), [
        durationMs,
        hasMore,
        transcriptRanges
    ]);
    const { setScrollEnabled } = useTranscriptScrollInfoContext();
    const onUserScroll = useCallback(()=>{
        setScrollEnabled(false);
    }, [
        setScrollEnabled
    ]);
    const { onScrolledProgrammatically } = useOnUserListScroll({
        listRef,
        onUserScroll
    });
    const { onSegmentPersistedInView } = useFetchSearchResultsForSegmentsInView();
    var _transcriptRanges_map;
    useFetchCurrentTranscriptChunk({
        durationMs,
        fetchedRanges: (_transcriptRanges_map = transcriptRanges.map((param)=>{
            let { startTimeMs, endTimeMs } = param;
            return {
                startTimeMs,
                endTimeMs
            };
        })) !== null && _transcriptRanges_map !== void 0 ? _transcriptRanges_map : [],
        fetchDataForRange: (params)=>{
            var _listRef_current;
            (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.createPositionsSnapshot();
            fetchTranscriptForRange(params);
        },
        hasMore
    });
    const fetchTranscriptForRangeForScrollDirection = useCallback(async (param)=>{
        let { startTimeMs, endTimeMs } = param;
        var _listRef_current;
        (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.createPositionsSnapshot();
        fetchTranscriptForRange({
            startTimeMs,
            endTimeMs
        });
    }, [
        fetchTranscriptForRange
    ]);
    const rowRenderer = useCallback((param)=>{
        let { index } = param;
        const segment = segments[index];
        if (segment.type === "empty") {
            var _listRef_current;
            return /*#__PURE__*/ _jsx(LoadingTranscriptSegments, {
                segment: segment,
                index: index,
                scrollElement: (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.getScrollElement(),
                fetchTranscriptForRange: fetchTranscriptForRangeForScrollDirection
            });
        } else {
            return /*#__PURE__*/ _jsx(TranscriptSegment, {
                segment: segment,
                onSegmentPersistedOnScreen: onSegmentPersistedInView
            });
        }
    }, [
        fetchTranscriptForRangeForScrollDirection,
        onSegmentPersistedInView,
        segments
    ]);
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": "TRANSCRIPT_SEGMENTS_LIST",
        className: "relative flex size-full flex-col overflow-hidden",
        children: /*#__PURE__*/ _jsx(TranscriptSegmentsVirtualizedList, {
            ref: listRef,
            currentLineObserver: /*#__PURE__*/ _jsx(CurrentLineObserver, {
                ref: currentWordObserverRef,
                segments: segments,
                listRef: listRef,
                onScrolledProgrammatically: onScrolledProgrammatically
            }),
            segments: segments,
            children: (param)=>{
                let { index } = param;
                return rowRenderer({
                    index
                });
            }
        })
    });
}));
TranscriptSegmentsList.displayName = "TranscriptSegmentsList";
